import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

type AttachmentChipProps = {
  label: string;
  url?: string;
  onDelete?: () => void;
  deleteIcon?: React.ReactElement;
};

const isImageUrl = (url: string): boolean =>
  /\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url);

const isPdfUrl = (url: string): boolean => /\.pdf(\?.*)?$/i.test(url);

export default function AttachmentChip({
  label,
  url,
  onDelete,
  deleteIcon,
}: AttachmentChipProps) {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);

  const canPreviewInDialog = Boolean(url && (isImageUrl(url) || isPdfUrl(url)));

  const handleClick = () => {
    if (!url) {
      return;
    }
    if (canPreviewInDialog) {
      setPreviewOpen(true);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Chip
        label={label}
        size="small"
        variant="outlined"
        clickable={Boolean(url)}
        onClick={url ? handleClick : undefined}
        onDelete={onDelete}
        deleteIcon={deleteIcon}
      />
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
          <Typography variant="subtitle1" sx={{ flex: 1, mr: 1 }} noWrap>
            {label}
          </Typography>
          <IconButton aria-label={t('Close')} onClick={() => setPreviewOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: 'grey.100' }}>
          {url && isImageUrl(url) && (
            <Box
              component="img"
              src={url}
              alt={label}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '70vh',
                margin: '0 auto',
              }}
            />
          )}
          {url && isPdfUrl(url) && (
            <Box
              component="iframe"
              src={url}
              title={label}
              sx={{ width: '100%', height: '70vh', border: 0 }}
            />
          )}
        </DialogContent>
        {url && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            >
              {t('Open in new tab')}
            </Button>
          </Box>
        )}
      </Dialog>
    </>
  );
}
